import { useEffect, useRef } from 'react'

type SwipeDirection = 'left' | 'right' | 'up' | 'down'

interface UseSwipeGestureOptions {
  ref: React.RefObject<HTMLElement>
  direction?: SwipeDirection
  threshold?: number
  /** 触发回调；参数为手势起点元素（pointerdown 目标），可用于区分滑动来源区域 */
  onSwipe?: (startTarget: Element | null) => void
  enabled?: boolean
  pointerTypes?: PointerEvent['pointerType'][]
}

const DEFAULT_THRESHOLD = 60
/** 方向锁定比率：目标方向位移需大于垂直/交叉方向的该倍数才锁定 */
const LOCK_RATIO = 1.1
/** 锁定最小位移（px）：未达此值不判定方向，避免微抖误锁 */
const LOCK_MIN_DELTA = 5

export function useSwipeGesture(options: UseSwipeGestureOptions) {
  const {
    ref,
    direction = 'left',
    threshold = DEFAULT_THRESHOLD,
    onSwipe,
    enabled = true,
    pointerTypes = ['touch'],
  } = options

  const onSwipeRef = useRef(onSwipe)

  useEffect(() => {
    onSwipeRef.current = onSwipe
  }, [onSwipe])

  useEffect(() => {
    const el = ref.current
    if (!el || !enabled) return

    const startXRef = { current: 0 }
    const startYRef = { current: 0 }
    const startTargetRef = { current: null as Element | null }
    const isTrackingRef = { current: false }
    const isTriggeredRef = { current: false }
    const pointerIdRef = { current: -1 }
    /** 方向锁定：一旦锁定，后续 move 不再因交叉分量放弃跟踪 */
    const directionLockedRef = { current: false }

    const isHorizontalTarget = direction === 'left' || direction === 'right'

    /**
     * 检查是否应锁定方向：
     * - 水平目标（left/right）：当 absX > LOCK_MIN_DELTA 且 absX/absY >= LOCK_RATIO 时锁定
     * - 垂直目标（up/down）：当 absY > LOCK_MIN_DELTA 且 absY/absX >= LOCK_RATIO 时锁定
     */
    const checkDirectionLock = (deltaX: number, deltaY: number): boolean => {
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)

      if (isHorizontalTarget) {
        if (absX <= LOCK_MIN_DELTA) return false
        // 无垂直分量时直接锁定
        if (absY === 0) return true
        return absX / absY >= LOCK_RATIO
      } else {
        if (absY <= LOCK_MIN_DELTA) return false
        if (absX === 0) return true
        return absY / absX >= LOCK_RATIO
      }
    }

    const checkSwipe = (deltaX: number, deltaY: number, directionLocked: boolean): boolean => {
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)

      // 未锁定时：检查方向比率，交叉分量过大则放弃
      if (!directionLocked) {
        if (isHorizontalTarget) {
          if (absY > 0 && absX / absY < LOCK_RATIO) return false
        } else {
          if (absX > 0 && absY / absX < LOCK_RATIO) return false
        }
      }

      switch (direction) {
        case 'left':
          return deltaX < 0 && absX >= threshold
        case 'right':
          return deltaX > 0 && absX >= threshold
        case 'up':
          return deltaY < 0 && absY >= threshold
        case 'down':
          return deltaY > 0 && absY >= threshold
      }
      return false
    }

    const cleanup = () => {
      isTrackingRef.current = false
      isTriggeredRef.current = false
      directionLockedRef.current = false
      pointerIdRef.current = -1
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerCancel)
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (!isTrackingRef.current || isTriggeredRef.current) return
      if (e.pointerId !== pointerIdRef.current) return

      const deltaX = e.clientX - startXRef.current
      const deltaY = e.clientY - startYRef.current

      // 方向锁定：检测到目标方向意图后立即锁定，锁定后不再因交叉分量放弃
      if (!directionLockedRef.current) {
        if (checkDirectionLock(deltaX, deltaY)) {
          directionLockedRef.current = true
        } else {
          // 未锁定且交叉分量过大 → 放弃跟踪（垂直滚动领地）
          const absX = Math.abs(deltaX)
          const absY = Math.abs(deltaY)
          if (isHorizontalTarget && absY > absX * LOCK_RATIO) {
            cleanup()
            return
          }
          if (!isHorizontalTarget && absX > absY * LOCK_RATIO) {
            cleanup()
            return
          }
          // 微抖阶段不判定，继续跟踪
          return
        }
      }

      if (checkSwipe(deltaX, deltaY, directionLockedRef.current)) {
        isTriggeredRef.current = true
        onSwipeRef.current?.(startTargetRef.current)
        cleanup()
      }
    }

    const handlePointerUp = (e: PointerEvent) => {
      if (!isTrackingRef.current || isTriggeredRef.current) { cleanup(); return }
      if (e.pointerId !== pointerIdRef.current) { cleanup(); return }

      const deltaX = e.clientX - startXRef.current
      const deltaY = e.clientY - startYRef.current

      // pointerup 时若尚未锁定，尝试锁定后再判定（处理快速滑动一次到位的场景）
      if (!directionLockedRef.current) {
        directionLockedRef.current = checkDirectionLock(deltaX, deltaY)
      }

      if (checkSwipe(deltaX, deltaY, directionLockedRef.current)) {
        isTriggeredRef.current = true
        onSwipeRef.current?.(startTargetRef.current)
      }
      cleanup()
    }

    const handlePointerCancel = () => {
      cleanup()
    }

    const handlePointerDown = (e: PointerEvent) => {
      if (!pointerTypes.includes(e.pointerType)) return
      if (isTrackingRef.current) return

      // 方案 B 修复 6：排除图片 resize handle 和图片操作菜单（含所有子孙按钮）
      //   handle 拖动和菜单点击都不应该触发整页滑动手势
      const target = e.target as HTMLElement
      if (
        target.closest('.inline-image-resize-handle') ||
        target.closest('.inline-image-size-controls')
      ) {
        // 命中则立即 stopImmediatePropagation，防止同层级其他 capture 监听器干扰
        try { e.stopImmediatePropagation() } catch (_) { /* ignore */ }
        return
      }

      startXRef.current = e.clientX
      startYRef.current = e.clientY
      startTargetRef.current = e.target as Element | null
      isTrackingRef.current = true
      isTriggeredRef.current = false
      directionLockedRef.current = false
      pointerIdRef.current = e.pointerId

      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerCancel)
    }

    // 使用 capture 阶段监听 pointerdown，确保在子元素（dnd-kit 等）之前收到事件
    el.addEventListener('pointerdown', handlePointerDown, true)

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerCancel)
    }
  }, [ref, direction, threshold, enabled, pointerTypes])
}
